import { Fragment } from 'react';
import { Link } from '@tanstack/react-router';
import {
  GoLiveRunway,
  PostureLegend,
  PostureUnitBar,
  ReadinessGauge,
  VERDICT_POSTURE,
} from '@/components/ui/charts';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';
import { cn } from '@/lib/utils/cn';
import { formatDate, formatDayMonth } from '@/lib/utils/format';
import type { HealthReadinessView } from '@/lib/api/health-readiness';
import type { ScheduleMilestone } from '@/lib/api/health-schedule';
import { BAND_BG_CLASS, POSTURE_LABEL, plural } from '../model';
import { useHrNavigate } from '../navigation';
import {
  daysUntil,
  gateDistance,
  localDayIso,
  milestoneSummary,
  runwayMilestones,
  timelineElapsed,
} from './overview-model';
import { BIG, CountUp, SUB, VerdictCard } from './parts';

/**
 * The Overview's three verdicts (prototype `postureCard()`,
 * `readinessCard()`, `countdownCard()`): the program posture as one cell
 * per domain, the go-live readiness gauge, and the days to go-live on the
 * kickoff-to-go-live runway. Every count and verdict is the API's.
 */

export function PostureCard({
  engagementId,
  view,
}: {
  engagementId: string;
  view: HealthReadinessView;
}) {
  const go = useHrNavigate(engagementId);
  const posture = view.verdict.posture;
  const counts = posture.counts;
  const status = posture.status;
  // The domains that set the posture: worst domain wins.
  const worst = view.domains.filter((d) => d.status === status);

  let sub;
  if (status === 'unassessed') {
    sub = `None of the ${plural(counts.total, 'domain is', 'domains is')} rated yet. Rate one to start the posture.`;
    if (counts.total === 0) sub = 'No domains yet. Add them from the Domains tab.';
  } else if (status === 'healthy') {
    sub = 'Every rated domain is green.';
  } else {
    sub = (
      <>
        Worst domain wins:{' '}
        {worst.map((d, i) => (
          <Fragment key={d.id}>
            {i > 0 ? ', ' : null}
            <Link
              to="/engagements/$engagementId/health-readiness/domains/$domainId"
              params={{ engagementId, domainId: d.id }}
              className="text-ink-900 underline underline-offset-2"
            >
              {d.name}
            </Link>
          </Fragment>
        ))}{' '}
        {worst.length === 1 ? 'is' : 'are'} {POSTURE_LABEL[status].toLowerCase()}.
      </>
    );
  }

  return (
    <VerdictCard label="Program posture now" band={status}>
      <div className={BIG} data-testid="posture-verdict">
        <span
          aria-hidden="true"
          className={cn(
            'h-3 w-3 flex-none rounded-full',
            status === 'unassessed' ? 'bg-ink-200' : BAND_BG_CLASS[status],
          )}
        />
        {POSTURE_LABEL[status]}
      </div>
      <p className={SUB}>{sub}</p>
      <PostureUnitBar
        motionKey="overview"
        units={view.domains.map((d) => ({
          id: d.id,
          name: d.name,
          posture: d.status,
          detail: d.ratedAt
            ? `rated ${formatDate(d.ratedAt)}${d.ratedBy ? ` by ${d.ratedBy}` : ''}`
            : null,
        }))}
        onSelect={(id) => go({ tab: 'domains', id })}
      />
      <PostureLegend
        counts={{
          critical: counts.critical,
          'at-risk': counts.atRisk,
          healthy: counts.healthy,
          unassessed: counts.unassessed,
        }}
      />
      <KpiSourcePill
        className="self-start"
        source={
          posture.lastRatedAt
            ? `From the domain ratings · last rated ${formatDayMonth(posture.lastRatedAt)} · counted by code`
            : 'No domain rated yet'
        }
      />
    </VerdictCard>
  );
}

export function ReadinessCard({ view }: { view: HealthReadinessView }) {
  const readiness = view.verdict.readiness;
  if (!readiness) {
    return (
      <VerdictCard label="Go-live readiness" band="unassessed">
        <div className={BIG}>Not in scope</div>
        <p className={SUB}>
          Go-Live Readiness is off for this engagement; turn it on to score criteria.
        </p>
      </VerdictCard>
    );
  }
  const { totalPercent, recommendation, thresholds, scoredCount, criteriaCount } = readiness;
  const value = scoredCount > 0 ? totalPercent : null;

  let sub;
  if (value === null) {
    sub = `Score the ${criteriaCount} criteria to see readiness. The AI proposes scores from the documents.`;
  } else if (!recommendation) {
    sub = `${scoredCount} of ${criteriaCount} criteria scored; the verdict waits for all ${criteriaCount}.`;
  } else {
    const { toGo, toConditional } = gateDistance(value, thresholds);
    if (recommendation === 'go') {
      sub = `${plural(-toGo, 'point', 'points')} above the Go gate.`;
    } else {
      sub = (
        <>
          <b className="font-semibold text-ink-900">{plural(toGo, 'point', 'points')} to Go</b> ·{' '}
          {recommendation === 'no_go'
            ? `${toConditional} to Conditional`
            : `${-toConditional} above Conditional`}
        </>
      );
    }
  }

  return (
    <VerdictCard
      label="Go-live readiness"
      band={recommendation ? VERDICT_POSTURE[recommendation] : 'unassessed'}
    >
      <ReadinessGauge
        motionKey="overview"
        value={value}
        thresholds={thresholds}
        verdict={recommendation}
        scored={scoredCount}
        total={criteriaCount}
      />
      <p className={cn(SUB, 'text-center')}>{sub}</p>
      <KpiSourcePill
        className="self-center"
        source={`${criteriaCount} weighted criteria · Go ${thresholds.go}% · Conditional ${thresholds.conditional}%`}
      />
    </VerdictCard>
  );
}

export function CountdownCard({
  view,
  milestones,
  loading = false,
  today,
}: {
  view: HealthReadinessView;
  milestones: ScheduleMilestone[];
  /** The schedule list is still on its way: say so, not "no milestones". */
  loading?: boolean;
  today: Date;
}) {
  const goLive = view.plannedGoLive;
  const start = view.projectStart;
  const summary = milestoneSummary(milestones);
  const band = summary.slipped > 0 ? 'at-risk' : milestones.length ? 'healthy' : 'unassessed';
  const days = goLive ? daysUntil(goLive, today) : null;
  const elapsed = goLive && start ? timelineElapsed(start, goLive, today) : null;

  return (
    <VerdictCard label="Planned go-live" band={band}>
      {days === null ? (
        <>
          <div className={cn(BIG, 'text-ink-300')}>—</div>
          <p className={SUB}>No planned go-live yet. It comes from the contract facts.</p>
        </>
      ) : (
        <>
          <div className={BIG}>
            <span>
              <CountUp value={Math.abs(days)} motionKey="overview" />
            </span>
            <span className="mb-1.5 self-end text-[15px] font-medium tracking-normal text-ink-500">
              {days >= 0 ? 'days to' : 'days since'} {formatDate(goLive)}
            </span>
          </div>
          <p className={SUB}>
            {elapsed === null
              ? 'Set the kickoff date to see how much of the timeline has passed.'
              : `${elapsed}% of the timeline from kickoff has passed.`}
          </p>
        </>
      )}
      {goLive && start ? (
        <GoLiveRunway
          motionKey="overview"
          start={start}
          goLive={goLive}
          today={localDayIso(today)}
          milestones={runwayMilestones(milestones, goLive)}
        />
      ) : null}
      <KpiSourcePill
        className="self-start"
        source={
          loading
            ? 'Reading the milestones…'
            : milestones.length
              ? `${plural(summary.slipped, 'milestone', 'milestones')} slipped · ${plural(summary.gatesAtRisk, 'IV&V gate', 'IV&V gates')} at risk`
              : 'No milestones yet · add them on the Schedule domain'
        }
      />
    </VerdictCard>
  );
}
